import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CollaboratorsPage } from '../collaborators/CollaboratorsPage'
import { DashboardPage } from '../dashboard/DashboardPage'
import { RulesPage } from '../rules/RulesPage'
import { SpacesPage } from '../spaces/SpacesPage'
import { DetailsPage } from '../details/DetailsPage'
import { ProfilePage } from '../profile/ProfilePage'
import { SettingsPage } from '../settings/SettingsPage'
import { useEscapeClose } from '../../lib/use-escape-close'

export type AdminView = 'dashboard' | 'profile' | 'collaborators' | 'trash' | 'spaces' | 'permissions' | 'rules' | 'details' | 'settings'

export type AdminSession = {
  organization: {
    id: string
    name: string
    slug: string
  }
  user: {
    id: string
    name: string
    email: string
    role: string
    status: string
    permissions: string[]
    rules: string[]
  }
  session: {
    token: string
    expiresAt: string
  }
  dashboard?: {
    organization: {
      id: string
      name: string
      slug: string
      status: string
      usersCount: number
    } | null
    access: {
      role: string
      status: string
      permissionsCount: number
      rulesCount: number
    }
    catalog: {
      rolesCount: number
      permissionsCount: number
      sessionsCount: number
      spacesCount?: number
    }
  } | null
}

type AdminShellProps = {
  session: AdminSession
  view: AdminView
  onNavigate: (view: AdminView) => void
  onLogout: () => void
  onSessionUpdate: (session: AdminSession) => void
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M12 3 19 6v6c0 5-3.1 8.6-7 11-3.9-2.4-7-6-7-11V6z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  )
}

function KeyIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="9" cy="12" r="3" />
      <path d="M12 12h8" />
      <path d="M18 12v3" />
      <path d="M16 12v2" />
    </svg>
  )
}

function TeamIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="9" cy="8" r="3" />
      <circle cx="17" cy="9" r="2.5" />
      <path d="M4 20a5 5 0 0 1 10 0" />
      <path d="M13 20a4 4 0 0 1 7 0" />
    </svg>
  )
}

function SettingsIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  )
}

function SidebarPanelIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 5h6v6H4z" />
      <path d="M14 5h6v6h-6z" />
      <path d="M4 13h6v6H4z" />
      <path d="M14 13h6v6h-6z" />
    </svg>
  )
}

function ProfileMenuIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <circle cx="12" cy="8" r="4" />
      <path d="M20 21a8 8 0 0 0-16 0" />
    </svg>
  )
}

function LogoutIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M10 17H5a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1h5" />
      <path d="M14 7l4 5-4 5" />
      <path d="M18 12H9" />
    </svg>
  )
}

function CollapseSidebarIcon({ collapsed }: { collapsed: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 5h16" />
      <path d="M4 12h16" />
      <path d="M4 19h16" />
      {collapsed ? <path d="m14 8 4 4-4 4" /> : <path d="m10 8-4 4 4 4" />}
    </svg>
  )
}

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 6h16" />
      <path d="M4 12h16" />
      <path d="M4 18h16" />
    </svg>
  )
}

function FullscreenIcon({ active }: { active: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      {active ? (
        <>
          <path d="M9 4v5H4" />
          <path d="M15 4v5h5" />
          <path d="M9 20v-5H4" />
          <path d="M15 20v-5h5" />
        </>
      ) : (
        <>
          <path d="M8 4H4v4" />
          <path d="M16 4h4v4" />
          <path d="M8 20H4v-4" />
          <path d="M16 20h4v-4" />
        </>
      )}
    </svg>
  )
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="M4 7h16" />
      <path d="M9 4h6" />
      <path d="M8 7l1 13h6l1-13" />
      <path d="M10 11v5" />
      <path d="M14 11v5" />
    </svg>
  )
}

function HistoryIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ width: '13px', height: '13px', marginRight: '6px', display: 'inline-block', verticalAlign: 'middle' }}>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  )
}

export function AdminShell({ session, view, onNavigate, onLogout, onSessionUpdate }: AdminShellProps) {
  const { t } = useTranslation()
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false)
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [focusedCollaboratorId, setFocusedCollaboratorId] = useState<string | null>(null)
  const userMenuRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const onDocumentClick = (event: MouseEvent) => {
      if (!userMenuRef.current) {
        return
      }

      if (!userMenuRef.current.contains(event.target as Node)) {
        setIsUserMenuOpen(false)
      }
    }

    document.addEventListener('mousedown', onDocumentClick)

    return () => {
      document.removeEventListener('mousedown', onDocumentClick)
    }
  }, [])

  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement))
    }

    onFullscreenChange()
    document.addEventListener('fullscreenchange', onFullscreenChange)

    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange)
    }
  }, [])

  useEscapeClose(isUserMenuOpen, () => {
    setIsUserMenuOpen(false)
  })

  useEscapeClose(isMobileMenuOpen, () => {
    setIsMobileMenuOpen(false)
  })

  const handleNavigate = (nextView: AdminView) => {
    setIsUserMenuOpen(false)
    setIsMobileMenuOpen(false)
    if (nextView !== 'collaborators') {
      setFocusedCollaboratorId(null)
    }
    onNavigate(nextView)
  }

  const handleLogout = () => {
    setIsUserMenuOpen(false)
    setIsMobileMenuOpen(false)
    onLogout()
  }

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen()
        return
      }

      await document.documentElement.requestFullscreen()
    } catch {
      setIsFullscreen(Boolean(document.fullscreenElement))
    }
  }

  return (
    <div
      className={`admin-shell${isSidebarCollapsed ? ' admin-shell--sidebar-collapsed' : ''}${
        isMobileMenuOpen ? ' admin-shell--mobile-menu-open' : ''
      }`}
    >
      {isMobileMenuOpen ? (
        <button
          type="button"
          className="admin-mobile-menu-backdrop"
          aria-label={t('admin.controls.mobileMenu.close')}
          onClick={() => setIsMobileMenuOpen(false)}
        />
      ) : null}
      <aside className="admin-sidebar">
        <div className="admin-sidebar__brand">
          <div className="admin-sidebar__mark">
            <img src="/logo.png" alt={t('app.title')} />
          </div>
        </div>

        <nav id="admin-mobile-navigation" className="admin-nav" aria-label={t('admin.nav.label')}>
          <button
            type="button"
            className={`admin-nav__item${view === 'dashboard' ? ' admin-nav__item--active' : ''}`}
            onClick={() => handleNavigate('dashboard')}
          >
            <SidebarPanelIcon />
            {t('admin.nav.dashboard')}
          </button>
          <button
            type="button"
            className={`admin-nav__item${view === 'details' ? ' admin-nav__item--active' : ''}`}
            onClick={() => handleNavigate('details')}
          >
            <HistoryIcon />
            {t('admin.nav.details')}
          </button>
          <button
            type="button"
            className={`admin-nav__item${view === 'collaborators' ? ' admin-nav__item--active' : ''}`}
            onClick={() => handleNavigate('collaborators')}
          >
            <TeamIcon />
            {t('admin.nav.team')}
          </button>
          <button
            type="button"
            className={`admin-nav__item${view === 'spaces' ? ' admin-nav__item--active' : ''}`}
            onClick={() => handleNavigate('spaces')}
          >
            <SidebarPanelIcon />
            {t('admin.nav.spaces')}
          </button>
          <button
            type="button"
            className={`admin-nav__item${view === 'rules' ? ' admin-nav__item--active' : ''}`}
            onClick={() => handleNavigate('rules')}
          >
            <KeyIcon />
            {t('admin.nav.rules')}
          </button>
          <button
            type="button"
            className={`admin-nav__item${view === 'permissions' ? ' admin-nav__item--active' : ''}`}
            onClick={() => handleNavigate('permissions')}
          >
            <ShieldIcon />
            {t('admin.nav.permissions')}
          </button>
          <button
            type="button"
            className={`admin-nav__item${view === 'trash' ? ' admin-nav__item--active' : ''}`}
            onClick={() => handleNavigate('trash')}
          >
            <TrashIcon />
            {t('admin.nav.trash')}
          </button>
          <button
            type="button"
            className={`admin-nav__item${view === 'settings' ? ' admin-nav__item--active' : ''}`}
            onClick={() => handleNavigate('settings')}
          >
            <SettingsIcon />
            Configurações
          </button>
        </nav>
      </aside>

      <section className="admin-main">
        <header className="admin-header">
          <div className="admin-header__controls">
            <button
              type="button"
              className="admin-header-button admin-header-button--mobile-menu"
              onClick={() => setIsMobileMenuOpen(true)}
              aria-label={t('admin.controls.mobileMenu.open')}
              aria-expanded={isMobileMenuOpen}
              aria-controls="admin-mobile-navigation"
              title={t('admin.controls.mobileMenu.open')}
            >
              <MenuIcon />
            </button>
            <button
              type="button"
              className="admin-header-button"
              onClick={() => setIsSidebarCollapsed((current) => !current)}
              aria-label={isSidebarCollapsed ? t('admin.controls.sidebar.expand') : t('admin.controls.sidebar.collapse')}
              title={isSidebarCollapsed ? t('admin.controls.sidebar.expand') : t('admin.controls.sidebar.collapse')}
            >
              <CollapseSidebarIcon collapsed={isSidebarCollapsed} />
            </button>
          </div>

          <div className="admin-header__meta" ref={userMenuRef}>
            <button
              type="button"
              className="admin-header-button"
              onClick={() => void toggleFullscreen()}
              aria-label={isFullscreen ? t('admin.controls.fullscreen.exit') : t('admin.controls.fullscreen.enter')}
              title={isFullscreen ? t('admin.controls.fullscreen.exit') : t('admin.controls.fullscreen.enter')}
            >
              <FullscreenIcon active={isFullscreen} />
            </button>
            <button
              type="button"
              className="admin-user-toggle"
              onClick={() => setIsUserMenuOpen((current) => !current)}
              aria-expanded={isUserMenuOpen}
              aria-haspopup="menu"
              aria-label={session.user.name}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path d="M20 21a8 8 0 0 0-16 0" />
                <circle cx="12" cy="8" r="4" />
              </svg>
            </button>

            {isUserMenuOpen ? (
              <div className="admin-user-menu" role="menu" aria-label={session.user.name}>
                <div className="admin-user-menu__identity">
                  <strong>{session.user.name}</strong>
                  <span>{session.user.email}</span>
                </div>

                <div className="admin-user-menu__separator" />

                <button type="button" className="admin-user-menu__item" onClick={() => handleNavigate('profile')}>
                  <ProfileMenuIcon />
                  {t('admin.nav.profile')}
                </button>
                <button type="button" className="admin-user-menu__item admin-user-menu__item--danger" onClick={handleLogout}>
                  <LogoutIcon />
                  {t('admin.nav.logout')}
                </button>
              </div>
            ) : null}
          </div>
        </header>

        <main className="admin-content">
          {view === 'dashboard' ? (
            <DashboardPage session={session} />
          ) : view === 'profile' ? (
            <ProfilePage session={session} onSessionUpdate={onSessionUpdate} />
          ) : view === 'settings' ? (
            <SettingsPage session={session} />
          ) : (
            view === 'collaborators' || view === 'trash' ? (
              <CollaboratorsPage
                key={view}
                session={session}
                scope={view === 'trash' ? 'trash' : 'active'}
                initialCollaboratorId={view === 'collaborators' ? focusedCollaboratorId : null}
                onInitialCollaboratorOpen={() => setFocusedCollaboratorId(null)}
              />
            ) : view === 'spaces' ? (
              <SpacesPage session={session} />
            ) : view === 'details' ? (
              <DetailsPage session={session} />
            ) : view === 'permissions' ? (
              <RulesPage key="permissions" session={session} resource="permissions" />
            ) : (
              <RulesPage key="rules" session={session} />
            )
          )}
        </main>

        <footer className="admin-footer">
          <span>{t('admin.footer.left')}</span>
          <a href="https://www.albatrosssystems.com.br" target="_blank" rel="noreferrer">
            {t('admin.footer.right')}
          </a>
        </footer>
      </section>
    </div>
  )
}
